import { Badge, Box, Checkbox, Flex, HStack, Text, VStack } from "@chakra-ui/react";

import { PatientId } from "../../../../shared/schema/schema";
import { dateFormatter } from "../../../../shared/utils/date-formatter";
import { getFullName } from "../../../../shared/utils/get-full-name";
import {
  distanceInMiles,
  isMappable,
  RnSchedulingMapPatient,
  SCHEDULING_STATUS_COLOR_SCHEME,
  SCHEDULING_STATUS_LABEL,
} from "../rn-scheduling-map.types";

type Props = {
  patients: RnSchedulingMapPatient[];
  selectedPatientId: PatientId | null;
  isChecked: (patientId: PatientId) => boolean;
  onSelectPatient: (patientId: PatientId | null) => void;
  onToggleChecked: (patientId: PatientId) => void;
};

export default function RnSchedulingMapPatientList(props: Props) {
  const selectedPatient =
    props.patients.find((patient) => patient.patientId === props.selectedPatientId) ?? null;

  const selectedLocation =
    selectedPatient !== null && isMappable(selectedPatient) ? selectedPatient.location : null;

  return (
    <VStack align="stretch" overflowY="auto" spacing={0}>
      {props.patients.map((patient) => {
        const isSelected = patient.patientId === props.selectedPatientId;
        const isChecked = props.isChecked(patient.patientId);

        const distanceLabel = (() => {
          if (selectedLocation === null || isSelected || !isMappable(patient)) {
            return null;
          }

          return `${distanceInMiles(selectedLocation, patient.location).toFixed(1)} mi away`;
        })();

        return (
          <Flex
            key={patient.patientId}
            _hover={{ bg: isSelected ? "blue.50" : "gray.50" }}
            align="flex-start"
            bg={isSelected ? "blue.50" : isChecked ? "green.50" : undefined}
            borderBottomWidth="1px"
            cursor="pointer"
            gap={2}
            px={3}
            py={2}
            onClick={() => props.onSelectPatient(isSelected ? null : patient.patientId)}
          >
            {/* The checkbox toggles the bulk selection without changing the map highlight. */}
            <Box pt="2px" onClick={(event) => event.stopPropagation()}>
              <Checkbox
                aria-label={`Select ${getFullName(patient)}`}
                isChecked={isChecked}
                onChange={() => props.onToggleChecked(patient.patientId)}
              />
            </Box>

            <Box flex={1} minWidth={0}>
              <Flex align="center" gap={2} justify="space-between">
                <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
                  {getFullName(patient)}
                </Text>
                <Text color="gray.600" flexShrink={0} fontSize="xs">
                  {dateFormatter.toDate(patient.certificationPeriodEndDate)}
                </Text>
              </Flex>

              <Text color="gray.500" fontSize="xs" noOfLines={1}>
                {patient.address ?? "No address"}
              </Text>

              <HStack mt={1} spacing={1}>
                <Badge
                  colorScheme={SCHEDULING_STATUS_COLOR_SCHEME[patient.schedulingStatus]}
                  fontSize="0.6rem"
                >
                  {SCHEDULING_STATUS_LABEL[patient.schedulingStatus]}
                </Badge>
                {!isMappable(patient) && (
                  <Badge colorScheme="orange" fontSize="0.6rem">
                    Not on map
                  </Badge>
                )}
                {distanceLabel !== null && (
                  <Text color="gray.500" fontSize="xs">
                    {distanceLabel}
                  </Text>
                )}
              </HStack>
            </Box>
          </Flex>
        );
      })}
    </VStack>
  );
}
